import {
	ContainerActionButtons,
	ContainerBody,
	EmailPreviewContainer,
	LeftSection,
	RightSection,
	ModalButton,
	ModalLabel,
	PreviewTitle,
	RichTextContainer,
	SelectTypeContainer,
	StyledQuillWrapper,
} from './RunLotteryModal.styles';
import { FC, useEffect, useMemo, useState } from 'react';
import { Calendar, CalendarWrapper, Input } from 'features/common/components/Form/Form.styles';
import { QUATERNARY, TERTIARY } from 'features/common/components/Button/constants';
import { useQuery } from '@tanstack/react-query';
import { getData } from 'services/api';
import ReactQuill from 'react-quill';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactDatePicker from 'react-datepicker';
import { DateTime } from 'luxon';
import 'react-quill/dist/quill.snow.css';

import { CheckboxButton } from 'features/common/components/CheckboxButton/CheckBoxButton.styles';
import FlexContainer from 'features/common/components/FlexContainer/FlexContainer';
import { IdParamsType } from 'utils/types';
import { LotteryEntriesCounts, LotteryEntryStatus } from '../../types';
import { Modal } from 'features/common/components/Modal/Modal';
import { RunLotteryModalProps } from './types';
import { getStatus } from './utils';
import { post } from 'services/api';
import { toastError } from 'hooks/useToastNotification/useToastNotification';
import { toastSuccess } from 'hooks/useToastNotification/useToastNotification';
import { DEFAULT_TEXT_ABOVE_BUTTON, DEFAULT_TEXT_BELOW_BUTTON } from './constants';

export const RunLotteryModal: FC<RunLotteryModalProps> = ({ showModal, closeModal, counts, refetch }) => {
	const { t } = useTranslation();
	const { id: eventId }: IdParamsType = useParams();

	const modules = useMemo(
		() => ({
			toolbar: [
				[{ header: [1, 2, 3, false] }],
				['bold', 'italic', 'underline', 'strike'],
				[{ color: [] }, { background: [] }],
				[{ list: 'ordered' }, { list: 'bullet' }],
				[{ align: [] }],
				['link', 'clean'],
			],
		}),
		[]
	);

	const formats = useMemo(
		() => ['header', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'bullet', 'align', 'link'],
		[]
	);

	const [typesSelected, setTypesSelected] = useState<LotteryEntryStatus[]>(['pending']);
	const [wheelchairSelected, setWheelchairSelected] = useState(false);
	const [numberOfWinners, setNumberOfWinners] = useState('');
	const [numberOfEntries, setNumberOfEntries] = useState('');
	const [expirationDate, setExpirationDate] = useState<Date>(() => {
		const d = new Date();
		d.setHours(d.getHours() + 72);
		return d;
	});
	const [disableLoserEmail, setDisableLoserEmail] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [maxWinners, setMaxWinners] = useState(0);
	const [maxEntries, setMaxEntries] = useState(0);
	const [textAboveButton, setTextAboveButton] = useState(DEFAULT_TEXT_ABOVE_BUTTON);
	const [textBelowButton, setTextBelowButton] = useState(DEFAULT_TEXT_BELOW_BUTTON);

	// Fetch event data for template variables
	const { data: eventData } = useQuery({
		queryKey: ['event', eventId],
		queryFn: () => getData(`/events/${eventId}`),
		enabled: !!eventId,
	});

	const { data: emailTemplate, isLoading: isLoadingPreview } = useQuery({
		queryKey: ['lotteryEmailPreview', eventId],
		queryFn: () => getData(`/lottery/templates/winner?event_id=${eventId}`),
		enabled: !!eventId,
	});

	// Reinterpret the picker's wall-clock as event-local time. The expiration is
	// stored in UTC (toUTC().toISO()) and rendered to the recipient in the event's
	// timezone (formatted string).
	const expirationInEventZone = useMemo(() => {
		const tz = eventData?.time_zone;
		const dt = DateTime.fromJSDate(expirationDate);
		return tz ? dt.setZone(tz, { keepLocalTime: true }) : dt;
	}, [expirationDate, eventData?.time_zone]);

	// Process the email template with current values
	const processedEmailPreview = useMemo(() => {
		if (!emailTemplate || !eventData) return emailTemplate;

		const eventDate = new Date(eventData.start_date).toLocaleString('en-US', {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: 'numeric',
			minute: 'numeric',
		});

		// Get the event image - prefer main_image, then first image, then empty
		const eventImg = eventData.images?.find((img: any) => img.main_image)?.image || eventData.images?.[0]?.image || '';

		const formattedExpirationDate = expirationInEventZone.toFormat('EEEE, MMMM d, yyyy h:mm a ZZZZ');

		// Replace variables in the rich text content
		const processedAboveText = textAboveButton
			.replace(/{{eventName}}/g, eventData.name)
			.replace(/{{eventDate}}/g, eventDate)
			.replace(/{{expirationDate}}/g, formattedExpirationDate);

		const processedBelowText = textBelowButton.replace(/{{expirationDate}}/g, formattedExpirationDate);

		return emailTemplate
			.replace(/{{eventName}}/g, eventData.name)
			.replace(/{{showDate}}/g, eventDate)
			.replace(/{{eventImg}}/g, eventImg)
			.replace(/{{{aboveButtonText}}}/g, processedAboveText)
			.replace(/{{{belowButtonText}}}/g, processedBelowText);
	}, [emailTemplate, eventData, textAboveButton, textBelowButton, expirationInEventZone]);

	useEffect(() => {
		if (counts && typesSelected.length > 0) {
			const maxTicketsSum = typesSelected.reduce(
				(acc, type) => acc + counts[(type + (wheelchairSelected ? '_wheelchair' : '')) as keyof LotteryEntriesCounts],
				0
			);
			const maxEntriesSum = typesSelected.reduce(
				(acc, type) =>
					acc + counts[(type + (wheelchairSelected ? '_wheelchair' : '') + '_entries') as keyof LotteryEntriesCounts],
				0
			);
			setMaxWinners(maxTicketsSum);
			setMaxEntries(maxEntriesSum);
		} else {
			setMaxWinners(0);
			setMaxEntries(0);
		}
	}, [counts, typesSelected, wheelchairSelected]);

	const handleRunLottery = async () => {
		setIsLoading(true);

		try {
			await post(`/events/${eventId}/lottery/run`, {
				number_of_winners: Number(numberOfWinners),
				number_of_entries: Number(numberOfEntries),
				status: typesSelected,
				wheelchair: wheelchairSelected,
				expiration_date: expirationInEventZone.toUTC().toISO(),
				disable_loser_email: disableLoserEmail,
				text_above_button: textAboveButton,
				text_below_button: textBelowButton,
			});

			refetch();
			toastSuccess('Lottery run successfully');
		} catch (error) {
			toastError('Error running lottery');
		} finally {
			closeModal();
			setIsLoading(false);
		}
	};

	const handleChangeNumberOfWinners = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = Number(e.target.value);
		if (!value) {
			setNumberOfWinners('');
		} else if (value && value <= maxWinners) {
			setNumberOfWinners(value.toString());
		}
	};

	const handleChangeNumberOfEntries = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = Number(e.target.value);
		if (!value) {
			setNumberOfEntries('');
		} else if (value && value <= maxEntries) {
			setNumberOfEntries(value.toString());
		}
	};

	const handleSelectType = (type: LotteryEntryStatus) => {
		if (typesSelected.includes(type)) {
			setTypesSelected(typesSelected.filter((t) => t !== type));
		} else {
			setTypesSelected([...typesSelected, type]);
		}
	};

	const handleOnChangeDate = (date: Date) => {
		setExpirationDate(date);
	};

	if (!showModal) return null;

	return (
		<Modal onClose={closeModal} isWide>
			<Modal.Header>Run Lottery</Modal.Header>
			<Modal.Body>
				<ContainerBody>
					<LeftSection>
						<FlexContainer flexDirection="column" gap="10px" width="100%">
							<ModalLabel variant="primary">Select status</ModalLabel>
							<FlexContainer gap="18px" width="100%">
								{['pending', 'not_winner', 'winner'].map((type) => (
									<SelectTypeContainer key={type} onClick={() => handleSelectType(type as LotteryEntryStatus)}>
										<CheckboxButton type="checkbox" checked={typesSelected.includes(type as LotteryEntryStatus)} />
										<ModalLabel variant="primary">{getStatus(type as LotteryEntryStatus)}</ModalLabel>
									</SelectTypeContainer>
								))}
							</FlexContainer>
						</FlexContainer>

						<FlexContainer flexDirection="column" gap="10px" width="100%">
							<ModalLabel variant="primary">Run only for</ModalLabel>
							<SelectTypeContainer key={'wheelchair'} onClick={() => setWheelchairSelected(!wheelchairSelected)}>
								<CheckboxButton type="checkbox" checked={wheelchairSelected} />
								<ModalLabel variant="primary">Wheelchair</ModalLabel>
							</SelectTypeContainer>
						</FlexContainer>

						<FlexContainer flexDirection="column" gap="10px" width="100%">
							<ModalLabel variant="primary">Disable email for</ModalLabel>
							<SelectTypeContainer key={'disable-loser-email'} onClick={() => setDisableLoserEmail(!disableLoserEmail)}>
								<CheckboxButton type="checkbox" checked={disableLoserEmail} />
								<ModalLabel variant="primary">Not winners</ModalLabel>
							</SelectTypeContainer>
						</FlexContainer>

						<FlexContainer gap="10px" width="100%">
							<FlexContainer flexDirection="column" gap="10px" width="100%">
								<ModalLabel variant="primary">Number of tickets (max {maxWinners})</ModalLabel>
								<Input
									type="number"
									placeholder="Enter number of tickets"
									style={{ color: 'black' }}
									value={numberOfWinners}
									onChange={handleChangeNumberOfWinners}
								/>
							</FlexContainer>

							<FlexContainer flexDirection="column" gap="10px" width="100%">
								<ModalLabel variant="primary">Number of entries (max {maxEntries})</ModalLabel>
								<Input
									type="number"
									placeholder="Enter number of winners"
									style={{ color: 'black' }}
									value={numberOfEntries}
									onChange={handleChangeNumberOfEntries}
								/>
							</FlexContainer>
						</FlexContainer>

						<FlexContainer flexDirection="column" gap="10px" width="100%">
							<ModalLabel variant="primary">Expiration date</ModalLabel>
							<CalendarWrapper isCalendarOpen={false}>
								<ReactDatePicker
									autoComplete="off"
									id={'expiration-date'}
									selected={expirationDate}
									onChange={handleOnChangeDate}
									showTimeSelect
									dateFormat="d MMMM, yyyy h:mm aa"
									calendarContainer={Calendar}
									data-cy="test"
								/>
							</CalendarWrapper>
						</FlexContainer>

						<RichTextContainer>
							<ModalLabel variant="primary">Text above claim button</ModalLabel>
							<StyledQuillWrapper>
								<ReactQuill
									theme="snow"
									value={textAboveButton}
									modules={modules}
									formats={formats}
									onChange={setTextAboveButton}
									placeholder="Enter text that will appear above the 'Click here to Claim Tickets!' button"
								/>
							</StyledQuillWrapper>
						</RichTextContainer>

						<RichTextContainer>
							<ModalLabel variant="primary">Text below claim button</ModalLabel>
							<StyledQuillWrapper>
								<ReactQuill
									theme="snow"
									value={textBelowButton}
									modules={modules}
									formats={formats}
									onChange={setTextBelowButton}
									placeholder="Enter text that will appear below the 'Click here to Claim Tickets!' button"
								/>
							</StyledQuillWrapper>
						</RichTextContainer>
					</LeftSection>

					<RightSection>
						<PreviewTitle>Email Preview</PreviewTitle>
						<EmailPreviewContainer>
							{isLoadingPreview ? (
								<div style={{ textAlign: 'center', padding: '20px', color: '#222' }}>Loading preview...</div>
							) : processedEmailPreview ? (
								<div dangerouslySetInnerHTML={{ __html: processedEmailPreview }} />
							) : (
								<div style={{ textAlign: 'center', padding: '20px', color: '#222' }}>Unable to load preview</div>
							)}
						</EmailPreviewContainer>
					</RightSection>
				</ContainerBody>
			</Modal.Body>
			<Modal.ActionButtons>
				<ContainerActionButtons>
					<ModalButton
						category={QUATERNARY}
						onClick={closeModal}
						text={t('button-cancel')}
						type="button"
						disabled={isLoading}
					/>
					<ModalButton
						category={TERTIARY}
						onClick={handleRunLottery}
						text={t('button-run-lottery')}
						type="button"
						disabled={isLoading || (!numberOfWinners && !numberOfEntries)}
					/>
				</ContainerActionButtons>
			</Modal.ActionButtons>
		</Modal>
	);
};
